import { useEffect, useRef, useState } from "react";

import {
  loginWithPassword,
  sendOtp,
  verifyOtp,
  sendPasswordRecoveryOtp,
  resetPassword,
} from "../../services/authService";

import { useAuth } from "../../context/AuthContext";

import "./LoginModal.css";



const STEPS = {
  Mobile: "mobile",
  OTP: "otp",
  PASSWORD: "password",
  RECOVERY: "recovery",
};

const OTP_RESEND_SECONDS = 90;

export default function LoginModal({ open, onClose, onLogin }) {
  const { login } = useAuth();

  const OTP_COOLDOWN_KEY = "otp_resend_until";


  const getRemainingSeconds = () => {
    const resendUntil = Number(localStorage.getItem(OTP_COOLDOWN_KEY) || 0);

    if (!resendUntil) {
      return 0;
    }

    const remaining = Math.ceil((resendUntil - Date.now()) / 1000);

    if (remaining <= 0) {
      localStorage.removeItem(OTP_COOLDOWN_KEY);
      return 0;
    }

    return remaining;
  };

  const startOtpCooldown = (seconds) => {
    const safeSeconds = Number(seconds);

    if (!safeSeconds || safeSeconds <= 0) {
      return;
    }

    const resendUntil = Date.now() + safeSeconds * 1000;

    localStorage.setItem(OTP_COOLDOWN_KEY, String(resendUntil));

    setResendSeconds(safeSeconds);
  };

  // =========================================================
  // STATE
  // =========================================================

  const [step, setStep] = useState(STEPS.Mobile);

  const [mobile, setMobile] = useState("");

  const [otp, setOtp] = useState("");

  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);

  const [resendingOtp, setResendingOtp] = useState(false);

  const [error, setError] = useState("");

  const [showPassword, setShowPassword] = useState(false);


  const [recoveryOtp, setRecoveryOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const [recoveryLoading, setRecoveryLoading] = useState(false);

  const [recoveryResendSeconds, setRecoveryResendSeconds] = useState(0);
  const [recoveryResending, setRecoveryResending] = useState(false);

  /*
   * This is the ONLY countdown state.
   *
   * 90 = 90 seconds remaining
   * 82 = 82 seconds remaining
   * 0  = resend button can appear
   */
  const [resendSeconds, setResendSeconds] = useState(0);

 // console.log("🔥 RENDER resendSeconds =", resendSeconds);

  const otpInputRef = useRef(null);
  const verifyingOtpRef = useRef(false);

  useEffect(() => {
    if (recoveryResendSeconds <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setRecoveryResendSeconds((seconds) => {
        if (seconds <= 1) {
          clearInterval(timer);
          return 0;
        }

        return seconds - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [recoveryResendSeconds]);


  const formatRecoveryResendTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remaining = seconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(
      remaining
    ).padStart(2, "0")}`;
  };

  // =========================================================
  // OTP COUNTDOWN
  // =========================================================
  useEffect(() => {
    const updateTimer = () => {
      const remaining = getRemainingSeconds();

      setResendSeconds(remaining);

      // Timer finished → remove cooldown error
      if (remaining <= 0) {
        setError((currentError) => {
          if (
            currentError === "لطفاً کمی صبر کنید." ||
            currentError?.includes("ثانیه صبر کنید")
          ) {
            return "";
          }

          return currentError;
        });
      }
    };

    updateTimer();

    const timer = setInterval(updateTimer, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);
  // =========================================================
  // RESET MODAL WHEN OPENED
  // =========================================================
  useEffect(() => {
    if (!open) {
      return;
    }

    setStep(STEPS.Mobile);

    setMobile("");

    setOtp("");

    setPassword("");

    setError("");

    setLoading(false);

    setResendingOtp(false);

    // IMPORTANT:
    // Restore the existing cooldown.
    setResendSeconds(getRemainingSeconds());
  }, [open]);
  // =========================================================
  // FOCUS OTP INPUT
  // =========================================================

  useEffect(() => {
    if (step !== STEPS.OTP) {
      return;
    }

    const timer = setTimeout(() => {
      otpInputRef.current?.focus();
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [step]);




  // =========================================================


  

  const handleOtpReceived = (code) => {
    const cleanCode = code
        .replace(/\D/g, "")
        .slice(0, 6);

    if (cleanCode.length !== 6) {
        return;
    }

    setOtp(cleanCode);

    verifyOtpAutomatically(cleanCode);
};



  useEffect(() => {
    if (step !== STEPS.OTP) {
        return;
    }

    if (!("OTPCredential" in window)) {
        return;
    }

    const controller = new AbortController();

    navigator.credentials
        .get({
            otp: {
                transport: ["sms"],
            },
            signal: controller.signal,
        })
        .then((credential) => {

            if (!credential?.code) {
                return;
            }

            handleOtpReceived(
                credential.code
            );

        })
        .catch((error) => {

            if (
                error?.name !== "AbortError"
            ) {
                console.log(
                    "Web OTP:",
                    error
                );
            }

        });

    return () => {
        controller.abort();
    };

}, [step]);




  // =========================================================
  // CLOSE MODAL
  // =========================================================

  if (!open) {
    return null;
  }

  // =========================================================
  // MOBILE HELPERS
  // =========================================================

  const normalizeMobile = (value) => {
    return value.replace(/\D/g, "").slice(0, 11);
  };

  const handleMobileChange = (e) => {
    setMobile(normalizeMobile(e.target.value));

    setError("");
  };

  // =========================================================
  // OTP HELPERS
  // =========================================================

  const verifyOtpAutomatically = async (otpValue) => {
    if (otpValue.length !== 6) {
      return;
    }

    if (verifyingOtpRef.current) {
      return;
    }

    verifyingOtpRef.current = true;

    try {
      setLoading(true);
      setError("");

      const result = await verifyOtp(
        mobile,
        otpValue
      );

      console.log(
        "AUTO VERIFY OTP RESULT:",
        result
      );

      if (!result.success) {
        setError(
          result.message ||
          "کد تایید صحیح نیست."
        );

        return;
      }

      handleSuccessfulLogin(result);

    } catch (error) {

      console.error(
        "AUTO VERIFY OTP ERROR:",
        error
      );

      setError(
        error.response?.data?.message ||
        "کد تایید صحیح نیست."
      );

    } finally {

      setLoading(false);

      verifyingOtpRef.current = false;
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value
      .replace(/\D/g, "")
      .slice(0, 6);

    setOtp(value);
    setError("");

    // Automatically verify when 6 digits are entered
    if (value.length === 6) {
      verifyOtpAutomatically(value);
    }
  };

  const formatResendTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);

    const remaining = seconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(remaining).padStart(
      2,
      "0",
    )}`;
  };

  // =========================================================
  // SEND OTP
  // =========================================================

  const handleSendOtp = async (e) => {
    e.preventDefault();

    if (!/^09\d{9}$/.test(mobile)) {
      setError("لطفاً شماره موبایل معتبر وارد کنید.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const result = await sendOtp(mobile);

      // console.log("🔥 SEND OTP RESULT =", result);

      // console.log("🔥 BEFORE SET =", Number(result.remaining_seconds || 0));

      // =========================================
      // SERVER REJECTED REQUEST
      // =========================================

      if (!result.success) {
        const remainingSeconds = Number(result.remaining_seconds || 0);

        if (remainingSeconds > 0) {
        //  console.log("🔥 SETTING TIMER TO =", remainingSeconds);

          setResendSeconds(remainingSeconds);
        }

        setError(result.message || "لطفاً کمی صبر کنید.");

        return;
      }

      // =========================================
      // OTP SENT SUCCESSFULLY
      // =========================================

 



      // startOtpCooldown(OTP_RESEND_SECONDS);

      setStep(STEPS.OTP);

      startOtpCooldown(
    Number(
        result.remaining_seconds ||
        OTP_RESEND_SECONDS
    )
);


    } catch (error) {
      console.error("SEND OTP ERROR:", error);

      const data = error.response?.data;

      const remainingSeconds = Number(data?.remaining_seconds || 0);

      if (remainingSeconds > 0) {
        setResendSeconds(remainingSeconds);
      }

      setError(data?.message || "ارسال کد تایید با خطا مواجه شد.");
    } finally {
      setLoading(false);
    }
  };
  // =========================================================
  // RESEND OTP
  // =========================================================

  const handleResendOtp = async () => {
    if (resendSeconds > 0 || resendingOtp) {
      return;
    }

    try {
      setResendingOtp(true);
      setError("");

      const result = await sendOtp(mobile);

     // console.log("RESEND RESULT:", result);

      if (!result.success) {
        const remainingSeconds = Number(result.remaining_seconds || 0);

        if (remainingSeconds > 0) {
          startOtpCooldown(remainingSeconds);
        }

        setError(result.message || "لطفاً کمی صبر کنید.");

        return;
      }

      const remainingSeconds = Number(result.remaining_seconds || 0);

      if (remainingSeconds > 0) {
        setResendSeconds(remainingSeconds);
      }

      setError(result.message || "لطفاً کمی صبر کنید.");
    } finally {
      setResendingOtp(false);
    }
  };
  // =========================================================
  // VERIFY OTP
  // =========================================================

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (otp.length !== 6) {
      setError(
        "لطفاً کد تایید ۶ رقمی را وارد کنید."
      );
      return;
    }

    await verifyOtpAutomatically(otp);
  };
  // =========================================================
  // PASSWORD LOGIN
  // =========================================================

  const handlePasswordLogin = async (e) => {
    e.preventDefault();

    if (!password) {
      setError("لطفاً رمز عبور را وارد کنید.");

      return;
    }

    try {
      setLoading(true);

      setError("");

      const result = await loginWithPassword(mobile, password);

      if (!result.success) {
        setError(result.message || "شماره موبایل یا رمز عبور اشتباه است.");

        return;
      }

      handleSuccessfulLogin(result);
    } catch (error) {
      console.error("PASSWORD LOGIN ERROR:", error);

      setError(
        error.response?.data?.message || "شماره موبایل یا رمز عبور اشتباه است.",
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // SUCCESSFUL LOGIN
  // =========================================================

  const handleSuccessfulLogin = (result) => {
    login(result.user, result.token);

    onLogin?.(result.user);

    onClose?.();
  };

  // =========================================================
  // SWITCH TO PASSWORD
  // =========================================================

  const switchToPassword = () => {
    setStep(STEPS.PASSWORD);

    setError("");

    setPassword("");
  };

  // =========================================================
  // SWITCH TO OTP
  // =========================================================

  const switchToOtp = () => {
    setStep(STEPS.OTP);

    setError("");

    setPassword("");
  };

  // =========================================================
  // BACK
  // =========================================================

  const handleBack = () => {
    if (step === STEPS.Mobile) {
      onClose();

      return;
    }

    if (step === STEPS.OTP || step === STEPS.PASSWORD) {
      setStep(STEPS.Mobile);

      setError("");
    }
  };




  // =========================================================
  // Recovery Password
  // =========================================================

  const handleRecoveryOtpChange = (e) => {
    setRecoveryOtp(
      e.target.value
        .replace(/\D/g, "")
        .slice(0, 6)
    );

    setError("");
  };


  const handleStartRecovery = async () => {

    if (!/^09\d{9}$/.test(mobile)) {
      setError(
        "لطفاً ابتدا شماره موبایل معتبر وارد کنید."
      );
      return;
    }

    try {

      setRecoveryLoading(true);
      setError("");

      const result =
        await sendPasswordRecoveryOtp(
          mobile
        );

      if (!result.success) {

        const remaining =
          Number(
            result.remaining_seconds || 0
          );

        if (remaining > 0) {
          startOtpCooldown(remaining);
        }

        setError(
          result.message ||
          "ارسال کد بازیابی با خطا مواجه شد."
        );

        return;
      }

      setRecoveryOtp("");

      setNewPassword("");

      setRepeatPassword("");

      startOtpCooldown(
        OTP_RESEND_SECONDS
      );

      setStep(
        STEPS.RECOVERY
      );

    } catch (error) {

      console.error(
        "PASSWORD RECOVERY OTP ERROR:",
        error
      );

      const data =
        error.response?.data;

      const remaining =
        Number(
          data?.remaining_seconds || 0
        );

      if (remaining > 0) {
        startOtpCooldown(remaining);
      }

      setError(
        data?.message ||
        "ارسال کد بازیابی با خطا مواجه شد."
      );

    } finally {

      setRecoveryLoading(false);

    }
  };



  const handleResetPassword = async (e) => {

    e.preventDefault();

    if (recoveryOtp.length !== 6) {

      setError(
        "لطفاً کد تایید ۶ رقمی را وارد کنید."
      );

      return;
    }

    if (!newPassword) {

      setError(
        "لطفاً رمز عبور جدید را وارد کنید."
      );

      return;
    }

    if (newPassword.length < 6) {

      setError(
        "رمز عبور باید حداقل ۶ کاراکتر باشد."
      );

      return;
    }

    if (newPassword !== repeatPassword) {

      setError(
        "تکرار رمز عبور با رمز عبور جدید یکسان نیست."
      );

      return;
    }

    try {

      setRecoveryLoading(true);
      setError("");

      const result =
        await resetPassword(
          mobile,
          recoveryOtp,
          newPassword,
          repeatPassword
        );

      if (!result.success) {

        setError(
          result.message ||
          "تغییر رمز عبور انجام نشد."
        );

        return;
      }

      /*
       * Password successfully changed.
       *
       * Go back to password login.
       */

      setPassword("");

      setNewPassword("");

      setRepeatPassword("");

      setRecoveryOtp("");

      setError("");

      setStep(
        STEPS.PASSWORD
      );

    } catch (error) {

      console.error(
        "RESET PASSWORD ERROR:",
        error
      );

      setError(
        error.response?.data?.message ||
        "تغییر رمز عبور با خطا مواجه شد."
      );

    } finally {

      setRecoveryLoading(false);

    }
  };


  const handleResendRecoveryOtp = async () => {

    if (
      resendSeconds > 0 ||
      recoveryLoading
    ) {
      return;
    }

    try {

      setRecoveryLoading(true);
      setError("");

      const result =
        await sendPasswordRecoveryOtp(
          mobile
        );

      if (!result.success) {

        const remaining =
          Number(
            result.remaining_seconds || 0
          );

        if (remaining > 0) {
          startOtpCooldown(remaining);
        }

        setError(
          result.message ||
          "ارسال مجدد کد با خطا مواجه شد."
        );

        return;
      }

      setRecoveryResendSeconds(
        Number(result.remaining_seconds || 90)
      );

      setStep(STEPS.RECOVERY_OTP);

      setRecoveryOtp("");

      startOtpCooldown(
        OTP_RESEND_SECONDS
      );

    } catch (error) {

      console.error(
        "RESEND RECOVERY OTP ERROR:",
        error
      );

      const remaining =
        Number(
          error.response?.data
            ?.remaining_seconds || 0
        );

      if (remaining > 0) {
        startOtpCooldown(remaining);
      }

      setError(
        error.response?.data?.message ||
        "ارسال مجدد کد با خطا مواجه شد."
      );

    } finally {

      setRecoveryLoading(false);

    }
  };


  const handleRecoveryResendOtp = async () => {

    if (
      recoveryResendSeconds > 0 ||
      recoveryResending
    ) {
      return;
    }

    try {

      setRecoveryResending(true);
      setError("");

      const result =
        await sendPasswordRecoveryOtp(mobile);

      console.log(
        "RECOVERY RESEND:",
        result
      );

      /*
       * Laravel 429
       */
      if (!result.success) {

        const remaining = Number(
          result.remaining_seconds || 0
        );

        if (remaining > 0) {
          setRecoveryResendSeconds(
            remaining
          );
        }

        setError(
          result.message ||
          "لطفاً کمی صبر کنید."
        );

        return;
      }


      /*
       * Successfully sent
       */
      setRecoveryOtp("");

      setRecoveryResendSeconds(
        Number(
          result.remaining_seconds || 90
        )
      );

    } catch (error) {

      console.error(
        "RECOVERY RESEND ERROR:",
        error
      );

      const data =
        error.response?.data;

      const remaining = Number(
        data?.remaining_seconds || 0
      );

      if (remaining > 0) {
        setRecoveryResendSeconds(
          remaining
        );
      }

      setError(
        data?.message ||
        "ارسال مجدد کد با خطا مواجه شد."
      );

    } finally {

      setRecoveryResending(false);

    }
  };

  



  // =========================================================
  // JSX
  // =========================================================

  return (
    <div
      className="login-modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="login-modal" dir="rtl">
        {/* =====================================================
                    HEADER
                ====================================================== */}

        <div className="login-modal__header">
          <button
            type="button"
            className="login-modal__close"
            onClick={onClose}
          >
            ×
          </button>

          <h2>ورود / ثبت نام</h2>
        </div>

        {/* =====================================================
                    STEP 1 - MOBILE
                ====================================================== */}

        {step === STEPS.Mobile && (
          <form className="login-modal__content" onSubmit={handleSendOtp}>
            <p className="login-modal__description">
              برای ورود یا ثبت‌نام شماره تلفن همراه خود را وارد کنید.
            </p>

            <div className="login-field">
              <label>شماره موبایل</label>

              <input
                type="tel"
                inputMode="numeric"
                dir="ltr"
                value={mobile}
                onChange={handleMobileChange}
                placeholder="09*********"
                autoFocus
                maxLength={11}
                style={{ textAlign: 'left' }}

              />
            </div>

            {error && resendSeconds <= 0 && (
              <div className="login-error">{error}</div>
            )}

            {resendSeconds > 0 && (
              <div className="login-error">
                لطفاً <strong>{resendSeconds}</strong> ثانیه صبر کنید.
              </div>
            )}

            <button
              type="submit"
              className="login-primary-button"
              disabled={loading}
            >
              {loading ? "در حال ارسال..." : "ادامه"}
            </button>
          </form>
        )}

        {/* =====================================================
                    STEP 2 - OTP
                ====================================================== */}

        {step === STEPS.OTP && (
          <form className="login-modal__content" onSubmit={handleVerifyOtp}>
            <p className="login-modal__description">
              لطفاً کد ارسال شده به شماره
              <strong> {mobile} </strong>
              را وارد کنید.
            </p>

            <div className="login-field">
              <label>کد یکبار مصرف</label>

              <input
                ref={otpInputRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                dir="ltr"
                value={otp}
                onChange={handleOtpChange}
                placeholder="******"
                maxLength={6}
                autoComplete="one-time-code"
                style={{ textAlign: 'left' }}
              />
            </div>

            {/* =================================================
                            RESEND
                        ================================================== */}

            <div className="otp-resend">
              {resendSeconds > 0 ? (
                <span className="otp-resend__timer">
                  ارسال مجدد کد تا{" "}
                  <strong>{formatResendTime(resendSeconds)}</strong>
                </span>
              ) : (
                <button
                  type="button"
                  className="otp-resend__button"
                  onClick={handleResendOtp}
                  disabled={resendingOtp}
                >
                  {resendingOtp ? "در حال ارسال..." : "ارسال مجدد کد"}
                </button>
              )}
            </div>
            {/* =================================================
                            ERROR
                        ================================================== */}
            {error && <div className="login-error">{error}</div>}
            {/* 
            {resendSeconds > 0 && (
              <div className="otp-resend otp-resend--mobile">
                <span className="otp-resend__timer">
                  ارسال مجدد کد تا{" "}
                  <strong>{formatResendTime(resendSeconds)}</strong>
                </span>
              </div>
            )} */}
            {/* =================================================
                            SECONDARY ACTIONS
                        ================================================== */}

            <div className="login-secondary-actions">
              <button type="button" onClick={switchToPassword}>
                ورود با رمز عبور
              </button>

              <button
                type="button"
                onClick={handleStartRecovery}
                disabled={recoveryLoading}
              >
                بازیابی رمز عبور
              </button>
            </div>

            {/* =================================================
                            PRIVACY
                        ================================================== */}

            <div className="login-privacy">
              <span>ⓘ</span>

              <p>
                استفاده از این سرویس به معنی پذیرش شرایط و قوانین این سرویس و
                قوانین حفظ حریم خصوصی کاربران است.
              </p>
            </div>

            {/* =================================================
                            FOOTER
                        ================================================== */}

            <div className="login-modal__footer">
              <button
                type="submit"
                className="login-primary-button"
                disabled={loading}
              >
                {loading ? "در حال بررسی..." : "ادامه"}
              </button>

              <button
                type="button"
                className="login-back-button"
                onClick={handleBack}
              >
                بازگشت
              </button>
            </div>
          </form>
        )}

        {/* =====================================================
                    STEP 3 - PASSWORD
                ====================================================== */}

        {step === STEPS.PASSWORD && (
          <form className="login-modal__content" onSubmit={handlePasswordLogin}>
            <p className="login-modal__description">
              لطفاً رمز عبور شماره همراه
              <strong> {mobile} </strong>
              را وارد کنید.
            </p>

            <div className="login-field">
              <label>رمز عبور</label>

              <div className="login-password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  dir="ltr"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);

                    setError("");
                  }}
                  autoFocus
                  autoComplete="current-password"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="password-toggle"
                  aria-label={
                    showPassword ? "مخفی کردن رمز عبور" : "نمایش رمز عبور"
                  }
                >
                  {showPassword ? "◉" : "◉̸"}
                </button>
              </div>
            </div>

            {error && <div className="login-error">{error}</div>}

            <div className="login-question">
              رمز عبور خود را فراموش کرده‌اید؟
            </div>

            <div className="login-secondary-actions">
              <button
                type="button"
                onClick={handleStartRecovery}
                disabled={recoveryLoading}
              >
                بازیابی رمز عبور
              </button>

              <button type="button" onClick={switchToOtp}>
                ورود با کد یکبار مصرف
              </button>
            </div>

            <div className="login-privacy">
              <span>ⓘ</span>

              <p>
                استفاده از این سرویس به معنی پذیرش شرایط و قوانین این سرویس و
                قوانین حفظ حریم خصوصی کاربران است.
              </p>
            </div>

            <div className="login-modal__footer">
              <button
                type="submit"
                className="login-primary-button"
                disabled={loading}
              >
                {loading ? "در حال بررسی..." : "ادامه"}
              </button>

              <button
                type="button"
                className="login-back-button"
                onClick={handleBack}
              >
                بازگشت
              </button>
            </div>
          </form>
        )}



        {/* =====================================================
            PASSWORD RECOVERY
        ====================================================== */}

        {step === STEPS.RECOVERY && (

          <form
            className="login-modal__content"
            onSubmit={handleResetPassword}
          >

            <h3 className="login-recovery-title">
              بازیابی رمز عبور
            </h3>


            <p className="login-modal__description">

              برای بازیابی رمز عبور، کد تایید
              ارسال شده به شماره موبایل خود را وارد کنید.

            </p>


            {/* =================================================
                    OTP
                ================================================= */}

            <div className="login-field login-recovery-otp">

              <label>
                کد دریافت شده
              </label>


              <div className="login-recovery-otp-row">

                <input
                  type="tel"
                  inputMode="numeric"
                  dir="ltr"
                  value={recoveryOtp}
                  onChange={handleRecoveryOtpChange}
                  placeholder="کد دریافت شده"
                  maxLength={6}
                  autoComplete="one-time-code"
                  style={{ textAlign: 'left' }}
                />



              </div>

            </div>


            {/* =================================================
                    NEW PASSWORD
                ================================================= */}

            <div className="login-field">

              <label>
                رمز عبور جدید
              </label>


              <div className="login-password-wrapper">

                <input
                  type={
                    showNewPassword
                      ? "text"
                      : "password"
                  }
                  dir="ltr"
                  value={newPassword}
                  onChange={(e) => {

                    setNewPassword(
                      e.target.value
                    );

                    setError("");

                  }}
                  placeholder="رمز عبور جدید"
                  autoComplete="new-password"
                />


                <button
                  type="button"
                  className="password-toggle"
                  onClick={() =>
                    setShowNewPassword(
                      value => !value
                    )
                  }
                >
                  {showNewPassword
                    ? "◉"
                    : "◉̸"
                  }
                </button>

              </div>

            </div>


            {/* =================================================
                    REPEAT PASSWORD
                ================================================= */}

            <div className="login-field">

              <label>
                تکرار رمز عبور جدید
              </label>


              <div className="login-password-wrapper">

                <input
                  type={
                    showRepeatPassword
                      ? "text"
                      : "password"
                  }
                  dir="ltr"
                  value={repeatPassword}
                  onChange={(e) => {

                    setRepeatPassword(
                      e.target.value
                    );

                    setError("");

                  }}
                  placeholder="تکرار رمز عبور جدید"
                  autoComplete="new-password"
                />


                <button
                  type="button"
                  className="password-toggle"
                  onClick={() =>
                    setShowRepeatPassword(
                      value => !value
                    )
                  }
                >
                  {showRepeatPassword
                    ? "◉"
                    : "◉̸"
                  }
                </button>

              </div>

            </div>




            {/* =================================================
                    ERROR
                ================================================= */}



            {/* ERROR */}



            {/* RESEND TIMER */}

            <div className="recovery-resend">

              {recoveryResendSeconds > 0 ? (

                <div className="recovery-resend__timer">
                  ارسال مجدد کد تا{" "}
                  <strong>
                    {formatRecoveryResendTime(
                      recoveryResendSeconds
                    )}
                  </strong>
                </div>

              ) : (

                <button
                  type="button"
                  className="recovery-resend__button"
                  onClick={handleRecoveryResendOtp}
                  disabled={recoveryResending}
                >
                  {recoveryResending
                    ? "در حال ارسال..."
                    : "ارسال مجدد کد"}
                </button>

              )}

            </div>





            {/* =================================================
                    PRIVACY
                ================================================= */}

            <div className="login-privacy">

              <span>
                ⓘ
              </span>

              <p>
                استفاده از این سرویس به معنی پذیرش
                شرایط و قوانین این سرویس و قوانین حفظ
                حریم خصوصی کاربران است.
              </p>

            </div>


            {/* =================================================
                    FOOTER
                ================================================= */}

            <div className="login-modal__footer">

              <button
                type="submit"
                className="login-primary-button"
                disabled={
                  recoveryLoading
                }
              >

                {recoveryLoading
                  ? "در حال بررسی..."
                  : "ادامه"
                }

              </button>


              <button
                type="button"
                className="login-back-button"
                onClick={() => {

                  setStep(
                    STEPS.PASSWORD
                  );

                  setError("");

                }}
              >
                بازگشت
              </button>

            </div>

          </form>

        )}
      </div>
    </div>
  );
}
